import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, XCircle, IndianRupee, Mail } from "lucide-react";
import { API, adminCall } from "@/utils/api";

const inr = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

const statusStyle = {
  pending: "text-warn border-warn",
  approved: "text-ok border-ok",
  rejected: "text-mute border-line",
};

const AdminVendors = ({ notices }) => {
  const [vendors, setVendors] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [depositForm, setDepositForm] = useState({});

  const load = useCallback(async () => {
    try {
      const [vRes, dRes] = await adminCall(() =>
        Promise.all([
          axios.get(`${API}/admin/vendors`, { withCredentials: true }),
          axios.get(`${API}/admin/deposits`, { withCredentials: true }),
        ])
      );
      setVendors(vRes.data);
      setDeposits(dRes.data);
    } catch (err) {
      console.error("Failed to load vendors", err);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (userId, status) => {
    try {
      await adminCall(() => axios.patch(`${API}/admin/vendors/${userId}/status`, { status }, { withCredentials: true }));
      toast.success(`Vendor ${status}.`);
      load();
    } catch {
      toast.error("Status update failed.");
    }
  };

  const recordDeposit = async (userId) => {
    const entry = depositForm[userId] || {};
    if (!entry.notice_id || !entry.amount) {
      toast.error("Pick a notice and enter the deposit amount.");
      return;
    }
    try {
      await adminCall(() =>
        axios.post(
          `${API}/admin/deposits`,
          { vendor_id: userId, notice_id: entry.notice_id, amount: parseFloat(entry.amount) },
          { withCredentials: true }
        )
      );
      toast.success("Security deposit recorded.");
      setDepositForm({ ...depositForm, [userId]: {} });
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not record deposit.");
    }
  };

  const sendReminder = async (userId) => {
    const entry = depositForm[userId] || {};
    if (!entry.notice_id) {
      toast.error("Pick a notice first, then send the reminder.");
      return;
    }
    try {
      await adminCall(() =>
        axios.post(
          `${API}/admin/deposits/remind`,
          { vendor_id: userId, notice_id: entry.notice_id },
          { withCredentials: true }
        )
      );
      toast.success("Deposit pending email sent to the vendor.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send reminder.");
    }
  };

  const liveNotices = notices.filter((n) => n.status === "live" && n.deposit_amount);

  return (
    <div className="mt-16" data-testid="admin-vendors-panel">
      <div className="mb-8 pb-6 border-b border-line">
        <p className="font-mono text-xs uppercase text-brand tracking-widest mb-2">Aadhaar Verification</p>
        <h2 className="font-display text-[clamp(24px,3vw,40px)] uppercase tracking-wide">
          Vendor Registrations
        </h2>
      </div>

      {vendors.length === 0 ? (
        <div className="border border-line p-8 bg-surface text-center" data-testid="vendors-empty">
          <p className="text-sm font-mono text-mute uppercase tracking-wider">
            No vendor registrations yet. New registrations from /register appear here for verification.
          </p>
        </div>
      ) : (
        <div className="space-y-6" data-testid="vendors-list">
          {vendors.map((v) => (
            <div key={v.user_id} className="bg-surface border border-line p-6" data-testid={`vendor-card-${v.user_id}`}>
              <div className="flex flex-col lg:flex-row gap-6">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3 flex-wrap">
                    <h3 className="text-base font-bold uppercase">{v.name}</h3>
                    <span className={`font-mono text-[10px] uppercase tracking-wider border px-2 py-1 ${statusStyle[v.status] || statusStyle.pending}`} data-testid={`vendor-status-${v.user_id}`}>
                      {v.status}
                    </span>
                  </div>
                  <div className="font-mono text-xs text-mute space-y-1">
                    <p>{v.email} · {v.phone}</p>
                    <p>{v.address}</p>
                    <p>
                      Property: <span className="text-ink uppercase">{v.property_type}</span>
                      {" · "}Aadhaar: <span className="text-ink">{v.aadhaar_masked}</span>
                    </p>
                  </div>

                  <div className="flex gap-4 mt-4">
                    {["front", "back"].map((side) => (
                      <a
                        key={side}
                        href={`${API}/admin/vendors/${v.user_id}/aadhaar/${side}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block border border-line hover:border-brand transition-colors"
                        data-testid={`aadhaar-${side}-${v.user_id}`}
                      >
                        <img
                          src={`${API}/admin/vendors/${v.user_id}/aadhaar/${side}`}
                          alt={`Aadhaar ${side} of ${v.name}`}
                          className="w-36 h-24 object-cover"
                          loading="lazy"
                        />
                        <span className="block text-center font-mono text-[10px] uppercase text-mute py-1">{side}</span>
                      </a>
                    ))}
                  </div>

                  {v.status === "pending" && (
                    <div className="flex gap-3 mt-4">
                      <button
                        onClick={() => setStatus(v.user_id, "approved")}
                        className="flex items-center gap-2 bg-brand hover:bg-brandhover text-night font-mono text-xs font-bold uppercase tracking-wider px-4 py-2 transition-colors"
                        data-testid={`vendor-approve-${v.user_id}`}
                      >
                        <ShieldCheck className="w-4 h-4" /> Approve
                      </button>
                      <button
                        onClick={() => setStatus(v.user_id, "rejected")}
                        className="flex items-center gap-2 border border-line hover:border-warn text-mute hover:text-warn font-mono text-xs uppercase tracking-wider px-4 py-2 transition-colors"
                        data-testid={`vendor-reject-${v.user_id}`}
                      >
                        <XCircle className="w-4 h-4" /> Reject
                      </button>
                    </div>
                  )}
                </div>

                {v.status === "approved" && (
                  <div className="lg:w-80 border-t lg:border-t-0 lg:border-l border-line pt-4 lg:pt-0 lg:pl-6" data-testid={`deposit-recorder-${v.user_id}`}>
                    <p className="font-mono text-[10px] uppercase tracking-wider text-mute mb-3">Record Security Deposit</p>
                    {liveNotices.length === 0 ? (
                      <p className="font-mono text-[11px] text-mute">No live notice with a deposit amount yet.</p>
                    ) : (
                      <div className="space-y-3">
                        <select
                          value={(depositForm[v.user_id] || {}).notice_id || ""}
                          onChange={(e) => setDepositForm({ ...depositForm, [v.user_id]: { ...(depositForm[v.user_id] || {}), notice_id: e.target.value } })}
                          className="w-full bg-night border border-line px-3 py-2 text-xs text-ink focus:border-brand outline-none font-mono"
                          data-testid={`deposit-notice-${v.user_id}`}
                        >
                          <option value="">Select notice...</option>
                          {liveNotices.map((n) => (
                            <option key={n.id} value={n.id}>
                              {n.district} — {n.point} ({inr(n.deposit_amount)})
                            </option>
                          ))}
                        </select>
                        <input
                          type="number"
                          min="1"
                          placeholder="Amount received (₹)"
                          value={(depositForm[v.user_id] || {}).amount || ""}
                          onChange={(e) => setDepositForm({ ...depositForm, [v.user_id]: { ...(depositForm[v.user_id] || {}), amount: e.target.value } })}
                          className="w-full bg-night border border-line px-3 py-2 text-xs text-ink focus:border-brand outline-none font-mono"
                          data-testid={`deposit-amount-${v.user_id}`}
                        />
                        <button
                          onClick={() => recordDeposit(v.user_id)}
                          className="flex items-center gap-2 bg-element hover:border-brand border border-line text-ink font-mono text-xs uppercase tracking-wider px-4 py-2 transition-colors w-full justify-center"
                          data-testid={`deposit-record-${v.user_id}`}
                        >
                          <IndianRupee className="w-4 h-4 text-brand" /> Mark Received
                        </button>
                        <button
                          onClick={() => sendReminder(v.user_id)}
                          className="flex items-center gap-2 border border-line hover:border-brand text-mute hover:text-brand font-mono text-xs uppercase tracking-wider px-4 py-2 transition-colors w-full justify-center"
                          data-testid={`deposit-remind-${v.user_id}`}
                        >
                          <Mail className="w-4 h-4" /> Email Deposit Pending
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {deposits.length > 0 && (
        <div className="mt-10" data-testid="deposits-register">
          <p className="font-mono text-xs uppercase text-brand tracking-widest mb-4">Deposit Register</p>
          <div className="border border-line">
            {deposits.map((d) => (
              <div key={d.id} className="flex flex-col md:flex-row md:items-center justify-between gap-2 px-6 py-4 border-b border-line last:border-b-0 bg-surface" data-testid={`deposit-row-${d.id}`}>
                <div>
                  <p className="text-sm font-bold uppercase">{d.vendor_name}</p>
                  <p className="font-mono text-[11px] text-mute">{d.notice_label}</p>
                </div>
                <div className="md:text-right">
                  <p className="font-mono text-sm text-ok font-bold">{inr(d.amount)}</p>
                  <p className="font-mono text-[10px] text-mute uppercase">
                    {new Date(d.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })} · {d.status}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminVendors;
